/**
 * The single funnel between input devices and gameplay — US 12.01.
 *
 * Sources push into the hub; the fixed-timestep loop drains one IntentFrame
 * per tick. Gameplay never sees a key code, pointer event or button id, and
 * adding an input source means writing one new source module, nothing else.
 */
import type { DiscreteIntent, IntentFrame, MoveVector } from '@shared/input.js';

export interface IntentSource {
  /** Current continuous movement contribution. */
  readonly move: MoveVector;
  detach(): void;
}

export class IntentHub {
  private sources: IntentSource[] = [];
  private queued: DiscreteIntent[] = [];

  attach(source: IntentSource): void {
    this.sources.push(source);
  }

  /** Called by sources when a discrete intent activates. */
  press(intent: DiscreteIntent): void {
    this.queued.push(intent);
  }

  /**
   * One frame per tick. Movement: the last source with non-zero input wins
   * (keyboard and touch never fight; whichever the player touched last is
   * simply the one producing non-zero values).
   */
  drain(): IntentFrame {
    let move: MoveVector = { x: 0, y: 0 };
    for (const s of this.sources) {
      if (s.move.x !== 0 || s.move.y !== 0) move = s.move;
    }
    const pressed = this.queued;
    this.queued = [];
    return { move, pressed };
  }

  detachAll(): void {
    for (const s of this.sources) s.detach();
    this.sources = [];
    this.queued = [];
  }
}
